import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Table, Tag, Tooltip, message } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api';

export default function OpLogs() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [reminder, setReminder] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadReminder = useCallback(() => {
    api.get('/op-logs/reminder')
      .then((res) => setReminder(res))
      .catch(() => setReminder(null));
  }, []);

  const load = useCallback((p = page) => {
    setLoading(true);
    api.get('/op-logs/asset', {
      params: { page: p, page_size: 20, keyword: keyword.trim() || undefined },
    })
      .then((res) => {
        setItems(res?.items || []);
        setTotal(res?.total || 0);
        setPage(p);
      })
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [keyword, page]);

  useEffect(() => {
    load(1);
    loadReminder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/op-logs/export`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      if (!r.ok) {
        let msg = '导出失败';
        try {
          const j = await r.json();
          msg = j.msg || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `op_logs_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('已导出到本地；系统已记录导出时间，约两个月后再次提醒');
      loadReminder();
    } catch (err) {
      message.error(err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const retentionDays = reminder?.retention_days || 180;
  const cycles = reminder?.retention_cycles || 3;
  const remindDays = reminder?.remind_days || 60;

  const columns = [
    {
      title: '时间',
      dataIndex: 'create_time',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    { title: '操作人', dataIndex: 'operate_user', width: 90 },
    {
      title: '操作对象',
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.asset_name || `资产ID ${r.asset_id}`}</div>
          <div style={{ fontSize: 12, color: '#888' }}>编号 {r.asset_code || '-'}</div>
        </div>
      ),
    },
    {
      title: '操作',
      dataIndex: 'operate_type',
      width: 110,
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: '详情（变更前后）',
      dataIndex: 'content',
      ellipsis: true,
      render: (v, r) => (
        <Tooltip title="点击查看完整详情">
          <a
            onClick={() => setDetail(r)}
            style={{ color: '#1677ff' }}
          >
            {v || '-'}
          </a>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>操作日志</h2>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
            记录资产名称/编号及变更前后；服务器保留约 {retentionDays} 天（{cycles} 个周期），建议每 {Math.round(remindDays / 30)} 个月导出到本地备份。
          </div>
        </div>
        <Space wrap>
          <Input.Search
            placeholder="搜索资产名称/编号/操作人/内容"
            allowClear
            style={{ width: 260 }}
            onSearch={() => load(1)}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { load(page); loadReminder(); }}>刷新</Button>
          <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            导出全部日志
          </Button>
        </Space>
      </div>

      {reminder?.need_remind ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="请立即导出操作日志备份（每两个月一次）"
          description={
            `${reminder.reason || ''} 更早于 ${reminder.oldest_keep_date || '-'} 的记录将被自动删除；导出到本地后不影响服务器滚动清理。`
          }
          action={(
            <Button size="small" type="primary" loading={exporting} onClick={handleExport}>
              立即导出
            </Button>
          )}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            reminder?.last_export_at
              ? `上次导出：${reminder.last_export_at}；距下次提醒约 ${Math.max(0, remindDays - (reminder.days_since_export || 0))} 天`
              : `日志约保留六个月（${cycles}×${remindDays}天）；建议每两个月导出一次`
          }
        />
      )}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: (p) => load(p),
          showTotal: (t) => `共 ${t} 条`,
        }}
      />

      <Modal
        open={!!detail}
        title="操作详情"
        footer={[
          <Button key="close" type="primary" onClick={() => setDetail(null)}>关闭</Button>,
        ]}
        onCancel={() => setDetail(null)}
        width={720}
      >
        {detail && (
          <div style={{ lineHeight: 1.8, fontSize: 14 }}>
            <div><b>时间：</b>{detail.create_time ? dayjs(detail.create_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</div>
            <div><b>操作人：</b>{detail.operate_user || '-'}</div>
            <div><b>操作类型：</b>{detail.operate_type || '-'}</div>
            <div><b>资产名称：</b>{detail.asset_name || '-'}</div>
            <div><b>资产编号：</b>{detail.asset_code || '-'}</div>
            <div><b>资产ID：</b>{detail.asset_id}</div>
            <div style={{ marginTop: 12 }}><b>完整详情：</b></div>
            <pre style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f7f7f7',
              padding: 12,
              borderRadius: 6,
              marginTop: 6,
              maxHeight: 360,
              overflow: 'auto',
            }}
            >
              {detail.content || '-'}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  );
}
